/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  NoticeCriteriaGroupActivity,
  StatusCode,
} from '../../../../../../../../api/enumerations';
import { createCriteriasGroup } from '../../../../../../../../api/theHive/criteriaGroups';
import { CreateCriteriasGroupData } from '../../../../../../../../api/theHive/criteriaGroups/types';
import { createTypologyGroup } from '../../../../../../../../api/theHive/typology';
import {
  BillingUnit,
  ReportType,
  TypologyCategory,
  TypologypropertyType,
  TypologyRuleKeyword,
} from '../../../../../../../../api/theHive/typology/enumerations';
import {
  TypologyData,
  TypologyGroupData,
  TypologyGroupItemData,
} from '../../../../../../../../api/theHive/typology/types';
import { SelectTextField } from '../../../../../../../../components/CustomInput';
import { WhiteCircularProgress } from '../../../../../../../../components/UI/Typography';
import {
  IconAddMS,
  IconDeleteMS,
} from '../../../../../../../../constants/icons';
import { selectGroupActivity } from '../../../../../../../../constants/selectOptions';
import { GlobalContext } from '../../../../../../../../context/global';
import useErrorMessage from '../../../../../../../../hooks/useErrorMessage';
import useGeneral from '../../../../../../../../hooks/useGeneral';
import {
  CancelButton,
  SaveButton,
  SubsectionTitle,
  TitleMoldal,
} from '../../styles';
import {
  ALL_SIZES,
  EditableTypologyGroupFields,
  ITypologyModalProps,
  ServiceGroup,
  Typology,
  TypologyGroup,
} from '../../types';
import {
  createEmptyService,
  createEmptyServiceGroup,
  createEmptyTypology,
} from '../../utils';
import CriteriaItem from './components/CriteriaItem';
import CriteriasDialog from './components/CriteriasDialog';
import ServiceItem from './components/ServiceItem';
import { useCriterias } from './hooks/useCriterias';
import {
  AddTypologyGroup,
  DialogContainer,
  StyledDialog,
  StyledPaper,
} from './styles';

export function AddTypologyModal({
  fetchTypologyGroups,
}: ITypologyModalProps): JSX.Element {
  const [isSaving, setIsSaving] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [serviceGroups, setServiceGroups] = useState<ServiceGroup[]>([
    createEmptyServiceGroup(),
  ]);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();
  const { notice } = useParams();
  const noticeId = parseInt(notice as string, 10);
  const { open, handleClose, handleOpen } = useGeneral();

  const safeGroupIndex = Math.min(selectedGroupIndex, serviceGroups.length - 1);
  const selectedGroup = serviceGroups[safeGroupIndex];
  const {
    loadingError,
    defaultCriterias,
    loadingCriterias,
    getDefaultCriteriasCallback,
  } = useCriterias(
    selectedGroup?.activity ?? NoticeCriteriaGroupActivity.SELECT
  );

  // ── updates imutáveis ────────────────────────────────
  const updateGroup = (
    groupIndex: number,
    updater: (group: ServiceGroup) => ServiceGroup
  ): void => {
    setServiceGroups((prev) =>
      prev.map((group, i) => (i === groupIndex ? updater(group) : group))
    );
  };

  const updateService = (
    groupIndex: number,
    serviceIndex: number,
    updater: (service: TypologyGroup) => TypologyGroup
  ): void => {
    updateGroup(groupIndex, (group) => ({
      ...group,
      services: group.services.map((service, i) =>
        i === serviceIndex ? updater(service) : service
      ),
    }));
  };

  const handleReset = (): void => {
    setServiceGroups([createEmptyServiceGroup()]);
    setSelectedGroupIndex(0);
  };

  const closeDialog = (): void => {
    handleClose();
    handleReset();
  };

  const scrollToService = (serviceId: number): void => {
    document
      .getElementById(`service-${serviceId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── grupo ────────────────────────────────────────────
  const handleAddGroup = (): void => {
    setServiceGroups((prev) => [...prev, createEmptyServiceGroup()]);
    setSelectedGroupIndex(serviceGroups.length);
  };

  const handleDeleteGroup = (groupIndex: number): void => {
    const updated = serviceGroups.filter((_, i) => i !== groupIndex);
    setServiceGroups(updated);
    setSelectedGroupIndex((prev) =>
      Math.max(0, Math.min(prev, updated.length - 1))
    );
  };

  const handleActivityChange = (value: NoticeCriteriaGroupActivity): void =>
    updateGroup(safeGroupIndex, (group) => ({
      ...group,
      activity: value,
      criteriaIds: [],
    }));

  const handleCriteriaSelection = (ids: number[]): void =>
    updateGroup(safeGroupIndex, (group) => ({ ...group, criteriaIds: ids }));

  const handleDeleteCriteria = (id: number): void =>
    updateGroup(safeGroupIndex, (group) => ({
      ...group,
      criteriaIds: group.criteriaIds.filter((item) => item !== id),
    }));

  // ── serviço ──────────────────────────────────────────
  const handleAddService = (): void =>
    updateGroup(safeGroupIndex, (group) => ({
      ...group,
      services: [...group.services, createEmptyService()],
    }));

  const handleDeleteService = (serviceIndex: number): void =>
    updateGroup(safeGroupIndex, (group) => ({
      ...group,
      services: group.services.filter((_, i) => i !== serviceIndex),
    }));

  const handleServiceChange = <K extends keyof EditableTypologyGroupFields>(
    serviceIndex: number,
    field: K,
    value: EditableTypologyGroupFields[K]
  ): void =>
    updateService(safeGroupIndex, serviceIndex, (service) => ({
      ...service,
      [field]: value,
    }));

  // ── tipo ─────────────────────────────────────────────
  const handleAddTypologyRow = (serviceIndex: number): void =>
    updateService(safeGroupIndex, serviceIndex, (service) => ({
      ...service,
      typologies: [...service.typologies, createEmptyTypology()],
    }));

  const handleDeleteTypology = (
    serviceIndex: number,
    typologyIndex: number
  ): void =>
    updateService(safeGroupIndex, serviceIndex, (service) => ({
      ...service,
      typologies: service.typologies.filter((_, i) => i !== typologyIndex),
    }));

  const handleTypologyChange = <K extends keyof Typology>(
    serviceIndex: number,
    typologyIndex: number,
    field: K,
    value: Typology[K]
  ): void =>
    updateService(safeGroupIndex, serviceIndex, (service) => ({
      ...service,
      typologies: service.typologies.map((typology, i) =>
        i === typologyIndex ? { ...typology, [field]: value } : typology
      ),
    }));

  const isServiceValid = (service: TypologyGroup): boolean => {
    const idValid =
      !!service.category &&
      !!service.code.trim() &&
      !!service.name.trim() &&
      !!service.reportType &&
      !!service.billingUnit &&
      !!service.priceType &&
      service.price > 0;
    if (!idValid || service.typologies.length === 0) return false;

    return service.typologies.every((typology) => {
      if (typology.type === TypologypropertyType.SELECT) return false;
      if (typology.range === TypologyRuleKeyword.SELECT) return false;
      if (typology.range === ALL_SIZES) return true;
      if (!typology.area) return false;
      if (typology.range === TypologyRuleKeyword.BETWEEN) {
        return !!typology.area2;
      }
      return true;
    });
  };

  const isValid = useMemo(
    () =>
      serviceGroups.every(
        (group) =>
          group.activity !== NoticeCriteriaGroupActivity.SELECT &&
          group.criteriaIds.length > 0 &&
          group.services.length > 0 &&
          group.services.every(isServiceValid)
      ),
    [serviceGroups]
  );

  const buildTypology = (typology: Typology): TypologyData => {
    if (typology.range === ALL_SIZES) {
      return {
        real_estate_type: typology.type,
        rule_keyword: TypologyRuleKeyword.OVER,
        rule_first_value: 1,
      };
    }
    return {
      real_estate_type: typology.type,
      rule_keyword: typology.range as TypologyRuleKeyword,
      rule_first_value: Number(typology.area),
      ...(typology.range === TypologyRuleKeyword.BETWEEN && {
        rule_second_value: Number(typology.area2),
      }),
    };
  };

  const buildService = (
    service: TypologyGroup,
    grouping: number
  ): TypologyGroupItemData => ({
    name: service.name,
    price: service.price,
    notice_id: noticeId,
    code: service.code,
    category: service.category as TypologyCategory,
    report_type: service.reportType as ReportType,
    billing_unit: service.billingUnit as BillingUnit,
    price_type: service.priceType,
    grouping,
    deadline_by_project: service.deadlineByProject,
    typologies: service.typologies.filter((t) => t.visible).map(buildTypology),

    ...(service.slaAlertDays > 0 && { sla_alert_days: service.slaAlertDays }),
    ...(!service.deadlineByProject &&
      service.slaLimitDays > 0 && { sla_limit_days: service.slaLimitDays }),
    ...(service.slaUseBusinessDay !== null && {
      sla_use_business_days: service.slaUseBusinessDay,
    }),
  });

  const handleSave = async (): Promise<void> => {
    if (!isValid) {
      setSnackbarMessage('Preencha todos os campos obrigatórios do grupo.');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    try {
      setIsSaving(true);

      for (let gi = 0; gi < serviceGroups.length; gi += 1) {
        const group = serviceGroups[gi];
        const grouping = gi + 1;

        const groupsData: TypologyGroupData = {
          typology_groups: group.services.map((s) => buildService(s, grouping)),
        };
        // eslint-disable-next-line no-await-in-loop
        const response = await createTypologyGroup(groupsData);
        if (response.detail.description)
          throw new Error(response.detail.description);
        if (response.detail.status_code !== StatusCode.OK || !response.data)
          throw new Error('Algo deu errado, tente novamente.');

        const savedIds = response.data.typology_groups_ids;
        if (savedIds.length !== group.services.length) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        const criteriaData: CreateCriteriasGroupData = {
          criteria_ids: group.criteriaIds,
          typology_group_ids: savedIds,
          activity: group.activity,
          name: `GRUPO ${grouping}`,
        };
        // eslint-disable-next-line no-await-in-loop
        const criteriaResponse = await createCriteriasGroup(
          noticeId,
          criteriaData
        );
        if (criteriaResponse.detail.description)
          throw new Error(criteriaResponse.detail.description);
        if (criteriaResponse.detail.status_code !== StatusCode.OK)
          throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage('Serviços criados com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      fetchTypologyGroups();
      closeDialog();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (loadingError) closeDialog();
  }, [loadingError]);

  return (
    <>
      <Button onClick={handleOpen}>adicionar serviços</Button>
      <StyledDialog open={open} onClose={() => setConfirmCloseOpen(true)}>
        <DialogContainer>
          <TitleMoldal>Adicionar serviços</TitleMoldal>
          <StyledPaper>
            <Box display="flex" gap={2} height="100%">
              {/* Navbar de grupos */}
              <Box
                sx={{
                  width: 210,
                  flexShrink: 0,
                  borderRight: '1px solid #D7D8DB',
                  pr: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  overflowY: 'auto',
                }}
              >
                {serviceGroups.map((group, index) => {
                  const active = index === safeGroupIndex;
                  return (
                    <Box
                      key={group.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        onClick={() => setSelectedGroupIndex(index)}
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          cursor: 'pointer',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          border: `1px solid ${
                            active ? theme.palette.secondary.main : '#D7D8DB'
                          }`,
                          backgroundColor: active
                            ? 'rgba(0,0,0,0.03)'
                            : 'transparent',
                        })}
                      >
                        <Typography
                          sx={{ fontSize: 13, fontWeight: active ? 600 : 400 }}
                        >
                          {`GRUPO ${index + 1}`}
                        </Typography>
                        {serviceGroups.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteGroup(index);
                            }}
                            sx={(theme) => ({
                              color: '#707070',
                              padding: '2px',
                              '&:hover': {
                                color: theme.palette.secondary.main,
                              },
                            })}
                          >
                            {IconDeleteMS}
                          </IconButton>
                        )}
                      </Box>
                      {active && (
                        <Box
                          sx={{
                            pl: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.25,
                          }}
                        >
                          {group.services.map((service, si) => (
                            <Typography
                              key={service.id}
                              onClick={() => scrollToService(service.id)}
                              sx={(theme) => ({
                                fontSize: 12,
                                cursor: 'pointer',
                                color: theme.palette.common.grey,
                                padding: '4px 8px',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  color: theme.palette.secondary.main,
                                  backgroundColor: 'rgba(0,0,0,0.03)',
                                },
                              })}
                            >
                              {service.code.trim() || `Serviço ${si + 1}`}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
                <AddTypologyGroup
                  onClick={handleAddGroup}
                  sx={{ marginTop: 1, padding: '10px 12px', minWidth: 0 }}
                >
                  {IconAddMS} adicionar grupo
                </AddTypologyGroup>
              </Box>

              {/* Grupo selecionado */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pl: 1,
                  pt: '12px',
                }}
              >
                {selectedGroup && (
                  <>
                    <Grid item container spacing={2}>
                      <Grid item xs={4}>
                        <SelectTextField
                          required
                          id={`activity-${selectedGroup.id}`}
                          label="conceito de espaço"
                          value={selectedGroup.activity}
                          setValue={(v) =>
                            handleActivityChange(
                              v as NoticeCriteriaGroupActivity
                            )
                          }
                          selectOptions={selectGroupActivity()}
                        />
                      </Grid>
                    </Grid>

                    <Grid item container mt="16px">
                      <SubsectionTitle>
                        Critério(s) de aceitação do grupo:
                      </SubsectionTitle>
                    </Grid>
                    <Grid item container>
                      {selectedGroup.criteriaIds.map((id) => (
                        <CriteriaItem
                          key={id}
                          criteriaId={id}
                          defaultCriteriasData={defaultCriterias}
                          onDeleteCriteria={handleDeleteCriteria}
                        />
                      ))}
                      <CriteriasDialog
                        criteriasData={defaultCriterias}
                        selectedCriterias={selectedGroup.criteriaIds}
                        handleSelection={handleCriteriaSelection}
                        loading={loadingCriterias}
                        disable={
                          selectedGroup.activity ===
                            NoticeCriteriaGroupActivity.SELECT ||
                          loadingCriterias ||
                          defaultCriterias.length === 0
                        }
                        activity={selectedGroup.activity}
                        getDefaultCriteriasCallback={
                          getDefaultCriteriasCallback
                        }
                      />
                    </Grid>

                    <Grid item container mt="24px" mb="8px">
                      <SubsectionTitle>Serviços:</SubsectionTitle>
                    </Grid>
                    {selectedGroup.services.map((service, serviceIndex) => (
                      <ServiceItem
                        key={service.id}
                        service={service}
                        domId={`service-${service.id}`}
                        serviceLabel={
                          service.code.trim() && service.name.trim()
                            ? `${service.code} - ${service.name}`
                            : `Serviço ${serviceIndex + 1}`
                        }
                        canDeleteService={selectedGroup.services.length > 1}
                        onServiceChange={(field, value) =>
                          handleServiceChange(serviceIndex, field, value)
                        }
                        onTypologyChange={(tIndex, field, value) =>
                          handleTypologyChange(
                            serviceIndex,
                            tIndex,
                            field,
                            value
                          )
                        }
                        onAddTypologyRow={() =>
                          handleAddTypologyRow(serviceIndex)
                        }
                        onDeleteTypology={(tIndex) =>
                          handleDeleteTypology(serviceIndex, tIndex)
                        }
                        onDeleteService={() =>
                          handleDeleteService(serviceIndex)
                        }
                      />
                    ))}
                    <AddTypologyGroup
                      onClick={handleAddService}
                      sx={{ marginTop: 1 }}
                    >
                      {IconAddMS} adicionar serviço
                    </AddTypologyGroup>
                  </>
                )}
              </Box>
            </Box>
          </StyledPaper>
          <Grid item container justifyContent="center" mt={2}>
            <Grid item mr={2}>
              <CancelButton onClick={() => setConfirmCloseOpen(true)}>
                Cancelar
              </CancelButton>
            </Grid>
            <Grid item ml={2}>
              <SaveButton onClick={handleSave} disabled={isSaving}>
                {isSaving ? <WhiteCircularProgress size={22} /> : 'Salvar'}
              </SaveButton>
            </Grid>
          </Grid>
        </DialogContainer>
      </StyledDialog>

      {/* Confirmação ao fechar */}
      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
      >
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            minWidth: 460,
          }}
        >
          <TitleMoldal>Deseja fechar?</TitleMoldal>
          <Typography sx={{ color: '#595959' }}>
            As alterações não salvas serão perdidas.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <CancelButton onClick={() => setConfirmCloseOpen(false)}>
              Não
            </CancelButton>
            <SaveButton
              onClick={() => {
                setConfirmCloseOpen(false);
                closeDialog();
              }}
            >
              Sim
            </SaveButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
